import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FiUsers, FiGlobe } from 'react-icons/fi';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { Button, buttonVariants } from '@components/Button';
import { Dropdown, borderedSelectStyles } from '@components/Dropdown';
import { addNotification, notificationStates } from '@components/Notification';
import { useTimezones } from '@/hooks/useTimezones';
import teamService from '@/services/team';
import { IS_DEMO_MODE } from '@/utils/demoMode';
import { DEMO_TEAM_OPTIONS } from '@/views/EmployeeList/EmployeeList.demoData';

import { SettingsPanelHeader } from './SettingsPanelHeader.component';
import {
  SettingsFieldCard,
  settingsFieldCardLayouts,
  SETTINGS_FIELD_CONTROL_WIDTH,
} from './SettingsFieldCard.component';

import styles from './TeamSettingsSection.module.scss';

const DEMO_TEAM_TIMEZONE = 'America/New_York';

const findTimezoneOption = (options, timeZone) =>
  timeZone
    ? options.find((option) => option.value === timeZone) || {
        value: timeZone,
        label: timeZone,
      }
    : null;

export const TeamSettingsSection = () => {
  const userProfile = useSelector((state) => state.auth?.profile);
  const companyId = userProfile?.activeCompany?.companyId;
  const userId = userProfile?.id;
  const role = userProfile?.activeCompany?.role || userProfile?.role;
  const activeTeamId = useSelector((state) => state.team?.active?.activeTeamid);

  const timezoneOptions = useTimezones();

  const [teamOptions, setTeamOptions] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const [selectedTimezone, setSelectedTimezone] = useState(null);
  const [savedTimezone, setSavedTimezone] = useState(null);
  const [loadingTimezone, setLoadingTimezone] = useState(false);

  const [saving, setSaving] = useState(false);

  // Load the teams this user is allowed to configure — every team in the
  // company for Admins, only their own team(s) for Managers (branches inside
  // teamService.getMemberTeamCollection based on `role`).
  useEffect(() => {
    let cancelled = false;

    const fetchTeams = async () => {
      if (IS_DEMO_MODE) {
        setTeamOptions(DEMO_TEAM_OPTIONS);
        setSelectedTeam(
          DEMO_TEAM_OPTIONS.find((option) => option.value === activeTeamId) ||
            DEMO_TEAM_OPTIONS[0] ||
            null
        );
        setLoadingTeams(false);
        return;
      }
      if (!companyId || !userId) return;
      setLoadingTeams(true);

      const res = await teamService.getMemberTeamCollection({
        companyId,
        userId,
        role,
      });

      if (cancelled) return;

      if (res.success) {
        const options = res.data.map((team) => ({
          value: team.docId,
          label: team.Team,
        }));
        setTeamOptions(options);
        setSelectedTeam(
          options.find((option) => option.value === activeTeamId) ||
            options[0] ||
            null
        );
      } else {
        addNotification({
          message: 'Failed to load teams. Please try again.',
          level: notificationStates.error,
        });
      }
      setLoadingTeams(false);
    };

    fetchTeams();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, userId, role]);

  // Fetch the selected team's saved timezone straight from teamDatabase —
  // not read off the option object, since the company-wide team list here
  // doesn't carry a timeZone field.
  useEffect(() => {
    let cancelled = false;

    const fetchTimezone = async () => {
      if (!selectedTeam?.value) {
        setSelectedTimezone(null);
        setSavedTimezone(null);
        return;
      }

      if (IS_DEMO_MODE) {
        setSelectedTimezone(
          findTimezoneOption(timezoneOptions, DEMO_TEAM_TIMEZONE)
        );
        setSavedTimezone(DEMO_TEAM_TIMEZONE);
        return;
      }

      setLoadingTimezone(true);

      const res = await teamService.getTeamTimezone(selectedTeam.value);
      if (cancelled) return;

      const timeZone = res.success ? res.timeZone : null;
      setSelectedTimezone(findTimezoneOption(timezoneOptions, timeZone));
      setSavedTimezone(timeZone);
      setLoadingTimezone(false);
    };

    fetchTimezone();
    return () => {
      cancelled = true;
    };
  }, [selectedTeam?.value, timezoneOptions]);

  const hasChanges = Boolean(
    selectedTeam && selectedTimezone && selectedTimezone.value !== savedTimezone
  );

  const handleCancel = () => {
    setSelectedTimezone(findTimezoneOption(timezoneOptions, savedTimezone));
  };

  const handleSaveChanges = async () => {
    if (!hasChanges || saving) return;
    setSaving(true);

    if (IS_DEMO_MODE) {
      setSavedTimezone(selectedTimezone.value);
      addNotification({
        message: `Timezone saved for "${selectedTeam.label}"`,
        level: notificationStates.success,
      });
      setSaving(false);
      return;
    }

    const res = await teamService.updateTeamTimezone(
      selectedTeam.value,
      selectedTimezone.value
    );

    if (res.success) {
      setSavedTimezone(selectedTimezone.value);
      addNotification({
        message: `Timezone saved for "${selectedTeam.label}"`,
        level: notificationStates.success,
      });
    } else {
      addNotification({
        message: res.error || 'Failed to save timezone. Please try again.',
        level: notificationStates.error,
      });
    }
    setSaving(false);
  };

  return (
    <>
      <SettingsPanelHeader
        icon={<FiUsers size={20} color="var(--indigo-600)" />}
        title="Team Settings"
        description="Manage preferences and configurations for your teams. Changes will apply to the selected team."
      />

      <SettingsFieldCard
        layout={settingsFieldCardLayouts.row}
        icon={<FiUsers size={16} color="var(--indigo-600)" />}
        label="Select Team"
        helperText="Choose the team you want to configure."
      >
        <Dropdown
          options={teamOptions}
          value={selectedTeam}
          onChange={setSelectedTeam}
          isLoading={loadingTeams}
          isDisabled={saving}
          width={SETTINGS_FIELD_CONTROL_WIDTH}
          selectStyles={borderedSelectStyles}
          placeholder="Select a team"
        />
      </SettingsFieldCard>

      <div className={styles.configHeading}>
        <Text
          variant={textVariantsEnum.Text_md}
          fontWeight={fontWeightEnum.Semibold}
          color="gray-900"
        >
          Team Configurations
        </Text>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Normal}
          color="gray-600"
        >
          Manage settings and preferences for the selected team.
        </Text>
      </div>

      <div className={styles.configFields}>
        <SettingsFieldCard
          layout={settingsFieldCardLayouts.row}
          icon={<FiGlobe size={16} color="var(--blue-600)" />}
          iconBg="blue-100"
          label="Timezone"
          helperText="Set the timezone for this team. This will be used for time tracking, reports and schedules."
        >
          <Dropdown
            options={timezoneOptions}
            value={selectedTimezone}
            onChange={setSelectedTimezone}
            isLoading={loadingTimezone}
            isDisabled={saving || !selectedTeam}
            width={SETTINGS_FIELD_CONTROL_WIDTH}
            selectStyles={borderedSelectStyles}
            placeholder="Not set — select a timezone"
          />
        </SettingsFieldCard>
      </div>

      <div className={styles.actionsRow}>
        <Button
          text="Cancel"
          variant={buttonVariants.secondary}
          onClick={handleCancel}
          disabled={saving}
        />
        <Button
          text="Save Changes"
          variant={buttonVariants.primary}
          onClick={handleSaveChanges}
          loading={saving}
          disabled={!hasChanges}
        />
      </div>
    </>
  );
};

TeamSettingsSection.displayName = 'TeamSettingsSection';
